import L from 'leaflet';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { STATUS_OPTIONS_DESCRIPTION } from '../constants/status';

// Fix para ícones quebrados no CRA (problema conhecido com webpack + leaflet)
export const fixLeafletIcon = () => {
  L.Marker.prototype.options.icon = L.icon({
    iconUrl,
    shadowUrl: iconShadow,
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });
};

// Ícones coloridos por situação da NSIR
const createColorIcon = (color) => L.icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export const markerIcons = {
  aguardando: createColorIcon('blue'),   // AGUARDANDO_INSPECAO / EM_INSPECAO
  finalizada: createColorIcon('green'),  // Finalizada sem anomalia
  anomalia:   createColorIcon('red'),    // Finalizada COM anomalia
  digitacao:  createColorIcon('orange'), // AGUARDANDO_DIGITACAO / EM_DIGITACAO
  executada:  createColorIcon('grey'),   // EXECUTADA_GDIS / EM_ANALISE / CANCELADA
};

// Lógica centralizada de cor do marcador individual
export const getMarkerIcon = (nsir) => {
  if (nsir.anomalia === true) return markerIcons.anomalia;
  if (['INSPECAO_FINALIZADA', 'INSPECAO_ENVIADA_COM_SUCESSO', 'DIGITACAO_FINALIZADA', 'FINALIZADA'].includes(nsir.status)) {
    return markerIcons.finalizada;
  }
  if (['AGUARDANDO_DIGITACAO', 'EM_DIGITACAO'].includes(nsir.status)) {
    return markerIcons.digitacao;
  }
  if (['EXECUTADA_GDIS', 'EM_ANALISE', 'CANCELADA'].includes(nsir.status)) {
    return markerIcons.executada;
  }
  return markerIcons.aguardando; // EM_CRIACAO, AGUARDANDO_INSPECAO, EM_INSPECAO, etc.
};

// Lógica centralizada de cor do cluster
export const getClusterClass = (cluster) => {
  const markers = cluster.getAllChildMarkers();
  const temAnomalia = markers.some(m => m.options.nsirData?.anomalia === true);
  if (temAnomalia) return 'cluster-anomalia';

  const temPendente = markers.some(m =>
    ['AGUARDANDO_INSPECAO', 'EM_INSPECAO', 'EM_CRIACAO'].includes(m.options.nsirData?.status)
  );
  if (temPendente) return 'cluster-pending';

  return 'cluster-ok';
};

// Cria o DivIcon customizado para o cluster
export const createCustomClusterIcon = (cluster) => {
  const count    = cluster.getChildCount();
  const cssClass = getClusterClass(cluster);

  let sizeClass = 'marker-cluster-small';
  if (count >= 10)  sizeClass = 'marker-cluster-medium';
  if (count >= 100) sizeClass = 'marker-cluster-large';

  return L.divIcon({
    html: `<div class="cluster-text"><span>${count}</span></div>`,
    className: `marker-cluster ${sizeClass} ${cssClass}`,
    iconSize: L.point(40, 40),
  });
};

export const MAP_SETTINGS = {
  center: [-18.5122, -44.5550],
  initialZoom: 6,
  minZoom: 4,
  maxZoom: 18,
  maxBounds: [
    [-33.74, -73.98],
    [5.27, -28.84]
  ],
  maxBoundsViscosity: 1.0,
  worldCopyJump: false,
  bounceAtZoomLimits: true,
};

export const validateCoordinate = (latitude, longitude) => {
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);
  const [[minLat, minLng], [maxLat, maxLng]] = MAP_SETTINGS.maxBounds;

  const foraDoBrasil =
    lat > maxLat || lat < minLat || lng > maxLng || lng < minLng;
  const latIgualLng = Math.abs(lat - lng) < 0.0001;

  if (isNaN(lat) || isNaN(lng)) {
    return { valid: false, lat, lng, motivo: 'Sem Coordenada' };
  }
  if (latIgualLng) {
    return { valid: false, lat, lng, motivo: 'Coordenadas Iguais' };
  }
  if (foraDoBrasil) {
    return { valid: false, lat, lng, motivo: 'Fora do Brasil' };
  }
  return { valid: true, lat, lng, motivo: null };
};

export const partitionByGpsValidity = (items, latKey = 'latitude', lngKey = 'longitude') => {
  const validas = [];
  const invalidas = [];

  (items || []).forEach((item) => {
    const result = validateCoordinate(item[latKey], item[lngKey]);
    if (result.valid) {
      validas.push({ ...item, lat: result.lat, lng: result.lng });
    } else {
      invalidas.push({ ...item, motivo: result.motivo });
    }
  });

  return { validas, invalidas };
};

export const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const buildNsirPopupHtml = (nsir) => {
  const status = STATUS_OPTIONS_DESCRIPTION[nsir.status] || nsir.status;
  const anomaliaBadge = nsir.anomalia
    ? '<span style="display:inline-block;margin-top:6px;padding:2px 8px;border-radius:4px;background:#f44336;color:#fff;font-size:11px;">Anomalia</span>'
    : '';

  return `
    <div style="font-family:Roboto,sans-serif;font-size:13px;line-height:1.4;min-width:200px;">
      <div style="font-weight:700;color:#ed6c02;margin-bottom:4px;">NSIR: ${escapeHtml(nsir.codigo)}</div>
      <div><strong>Status:</strong> ${escapeHtml(status)}</div>
      <div><strong>Serviço:</strong> ${escapeHtml(nsir.tipoServico)} - ${escapeHtml(nsir.subtipo)}</div>
      <div><strong>Equipamento:</strong> ${escapeHtml(nsir.equipamento)}</div>
      ${anomaliaBadge}
      <a href="/nsir/${encodeURIComponent(nsir.id)}" target="_blank" rel="noopener noreferrer"
         style="display:block;margin-top:8px;color:#ed6c02;font-size:12px;text-decoration:none;">
        Ver detalhes completos →
      </a>
    </div>
  `;
};
